import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, Calculator, Pencil } from "lucide-react";
import DocxUploader from "@/components/DocxUploader";
import DocumentAttachZone from "@/components/DocumentAttachZone";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import Carto360Button from "@/components/Carto360Button";
import { useDemoMode } from "@/lib/demoMode";
import { ObligationsPanel } from "@/components/ObligationsPanel";

function color(score) {
  if (score >= 12) return "var(--risk-high)";
  if (score >= 6) return "var(--risk-medium)";
  return "var(--risk-low)";
}

function Node({ label, value, type, level = 0, children }) {
  const score = typeof value === "number" ? value : 0;
  const c = level === 0 ? "var(--risk-info)" : color(score);
  return (
    <div className={`relative ${level > 0 ? "ml-6 pl-5 border-l border-white/10" : ""}`}>
      <div className="flex items-center gap-3 py-3">
        <div
          className="w-9 h-9 rounded-md border flex items-center justify-center text-sm font-semibold"
          style={{ borderColor: `${c}55`, background: `${c}1A`, color: c }}
        >
          {value}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white">{label}</div>
          <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
            {type === "calcule" && <><Calculator className="w-3 h-3" /> calculé</>}
            {type === "saisie" && <><Pencil className="w-3 h-3" /> saisie manuelle</>}
            {!type && level === 0 && "entité racine"}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function RiskDetail() {
  const { id } = useParams();
  const isDemo = useDemoMode();
  const endpoint = isDemo ? "/risks" : "/workspace/risks";
  const linkBase = isDemo ? "/demo/risques" : "/app/risques";
  const [risk, setRisk] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`${endpoint}/${id}`)
      .then((r) => setRisk(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Erreur"));
  }, [id, endpoint]);

  if (error) return <div className="text-zinc-400">{error}</div>;
  if (!risk) return <div className="text-zinc-400">Chargement…</div>;

  // Build a default "ontologie" payload if missing (workspace risks don't have it yet)
  const safeRisk = risk.ontologie ? risk : {
    ...risk,
    ontologie: {
      racine: { label: risk.title, value: risk.impact_net * risk.probabilite_nette },
      fils: [
        { label: "Impact net", value: risk.impact_net, type: "saisie" },
        { label: "Probabilité nette", value: risk.probabilite_nette, type: "saisie" },
        { label: "Contrôles en place", value: risk.controles, type: "saisie" },
        { label: "Plans d'action", value: risk.plans_action, type: "saisie" },
      ],
    },
  };

  const brut = risk.impact_brut * risk.probabilite_brute;
  const net = risk.impact_net * risk.probabilite_nette;
  const ontologyFields = risk.ontology?.fils || [];
  const netSourceMismatch = ["Impact net", "Probabilité nette"].some((label) => {
    const ontologyValue = ontologyFields.find((field) => field.label === label)?.value;
    const registerValue = label === "Impact net" ? risk.impact_net : risk.probabilite_nette;
    return ontologyValue !== null && ontologyValue !== undefined && registerValue !== null && registerValue !== undefined &&
      Number.isFinite(Number(ontologyValue)) && Number.isFinite(Number(registerValue)) && Number(ontologyValue) !== Number(registerValue);
  });

  return (
    <div className="space-y-5" data-testid="risk-detail-root">
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <Link to={linkBase} className="hover:text-white">Risques</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-zinc-300">{risk.code}</span>
        {!isDemo && (
          <div className="ml-auto">
            <Carto360Button subjectType="risque" subjectId={id} compact />
          </div>
        )}
      </div>

      {/* W11-GOV — Risk 360 : provenance + verbatim + décision humaine, l'écran où l'auditeur atterrit */}
      {risk.provenance && (
        <div className="noam-card p-5 border border-[#7F77DD]/30" data-testid="risk-provenance-card">
          <div className="noam-overline mb-2 text-[#7F77DD]">Provenance — chaîne de preuve</div>
          <div className="text-sm text-zinc-200 italic">« {risk.provenance.excerpt} »</div>
          <div className="text-xs text-zinc-400 mt-2 space-y-0.5">
            <div>Source : <b className="text-zinc-300">{risk.provenance.document_name || risk.provenance.source_label || "Texte fourni"}</b> · validé par revue humaine</div>
            {risk.provenance.justification && <div>Justification : {risk.provenance.justification}</div>}
            {risk.provenance.extracted_at && <div>Extrait le {new Date(risk.provenance.extracted_at).toLocaleDateString("fr-CH")} — {risk.provenance.extractor}</div>}
          </div>
        </div>
      )}

      {/* W11-GOV — Prochain pas : contrôle, plan, responsable (P-1) */}
      {!isDemo && (
        <div className="noam-card p-5" data-testid="risk-next-steps">
          <ObligationsPanel system="governance" refType="risk" refId={id} refLabel={risk.code}
            title="Prochains pas — contrôles, plans, responsables" />
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 space-y-5">
          <div className="noam-card p-6">
            <div className="noam-overline mb-2">{risk.categorie}</div>
            <h1 className="noam-display text-2xl font-bold tracking-tight">{risk.title}</h1>
            <div className="mt-1 font-mono text-xs text-zinc-500">{risk.code}</div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-white/5 rounded-lg p-4">
                <div className="text-xs text-zinc-400">Risque Brut</div>
                <div
                  className="noam-display text-3xl font-bold mt-2"
                  style={{ color: color(brut) }}
                  data-testid="risk-brut-score"
                >
                  {brut}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  I {risk.impact_brut} × P {risk.probabilite_brute}
                </div>
              </div>
              <div className="border border-white/5 rounded-lg p-4">
                <div className="text-xs text-zinc-400">Risque Net</div>
                <div
                  className="noam-display text-3xl font-bold mt-2"
                  style={{ color: color(net) }}
                  data-testid="risk-net-score"
                >
                  {net}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  I {risk.impact_net} × P {risk.probabilite_nette}
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-zinc-500">Owner</div>
                <div>{risk.owner}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Tendance</div>
                <div className="capitalize">{risk.tendance}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Contrôles</div>
                <div>{risk.controles}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Plans d'action</div>
                <div>{risk.plans_action}</div>
              </div>
            </div>
          </div>
        </div>

        {netSourceMismatch && (
            <div role="alert" className="lg:col-span-12 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="risk-net-source-warning">
              Les valeurs nettes de la structure diffèrent de celles utilisées pour calculer le risque net. Le score affiché suit le registre des risques. Vérifiez ces valeurs avant de vous appuyer sur le résultat.
            </div>
          )}
          <div className="lg:col-span-7">
          <div className="noam-card p-6" data-testid="risk-ontology-tree">
            <div className="noam-overline mb-1">Ontologie</div>
            <div className="noam-display text-base font-semibold mb-4">Structure brut/net</div>
            <Node label={safeRisk.ontologie.racine.label} value={safeRisk.ontologie.racine.value}>
              {safeRisk.ontologie.fils.map((n, idx) => (
                <Node key={idx} label={n.label} value={n.value} type={n.type} level={1}>
                  {n.fils?.map((nn, jdx) => (
                    <Node key={jdx} label={nn.label} value={nn.value} type="saisie" level={2} />
                  ))}
                </Node>
              ))}
            </Node>
          </div>

          <div className="noam-card p-6 mt-5">
            <DocxUploader module="risque" targetId={id} title="Pièces jointes" />
          </div>

          <div className="mt-5">
            <DocumentAttachZone module="risques" entityId={id} entityLabel={risk.title} />
          </div>

          <div className="mt-5">
            <HybridResearchPanel
              contextModule="generic"
              initialTopic={risk.title || ""}
              defaultMode="hybrid"
              title="Analyser ce risque face au marché"
              testid="risk-hybrid-research"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
